import { useState, useEffect } from 'react'
import {
  Save, AlertCircle, CheckCircle, Info, AlertTriangle,
  Clock, Target, Shield, Bell, Moon, Sun, RefreshCw,
  Search, Zap, HelpCircle
} from 'lucide-react'
import { api, FullConfig, Recommendation, BanStatus } from '../lib/api'

// Tooltip component for parameter explanations
function Tooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-block ml-1">
      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
      <div className="hidden group-hover:block absolute z-10 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg -top-2 left-6">
        {text}
        <div className="absolute top-3 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
      </div>
    </div>
  )
}

// Recommendation card component
function RecommendationCard({ rec }: { rec: Recommendation }) {
  const styles = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info },
    warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: AlertTriangle },
    error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: AlertCircle }
  }
  const style = styles[rec.type]
  const Icon = style.icon

  return (
    <div className={`${style.bg} ${style.border} border rounded-lg p-3`}>
      <div className="flex items-start">
        <Icon className={`h-5 w-5 ${style.text} mr-2 flex-shrink-0 mt-0.5`} />
        <div>
          <p className={`text-sm font-medium ${style.text}`}>{rec.message}</p>
          <p className="text-xs text-gray-600 mt-1">💡 {rec.suggestion}</p>
        </div>
      </div>
    </div>
  )
}

// Ban status indicator
function BanStatusCard({ status }: { status: BanStatus }) {
  if (!status.is_banned && !status.backoff_active) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-800 font-medium">Sin bloqueos detectados</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
          <div>
            <span className="text-red-800 font-medium">
              {status.ban_type || 'Bloqueo detectado'}
            </span>
            {status.backoff_active && (
              <p className="text-xs text-red-600 mt-1">
                Backoff activo: {Math.ceil(status.backoff_remaining_seconds / 60)} min restantes
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Settings() {
  const [config, setConfig] = useState<FullConfig | null>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [banStatus, setBanStatus] = useState<BanStatus | null>(null)
  const [currentWindow, setCurrentWindow] = useState<'normal' | 'critical'>('normal')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Fetch configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.getFullConfig()
        setConfig(response.config)
        setRecommendations(response.recommendations)
        setBanStatus(response.ban_status)
        setCurrentWindow(response.current_window)
      } catch (err) {
        console.error('Error fetching config:', err)
        setMessage({ type: 'error', text: 'Error al cargar configuración' })
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!config) return

    setSaving(true)
    setMessage(null)

    try {
      const response = await api.updateFullConfig(config)
      setConfig(response.config)
      setRecommendations(response.recommendations)
      setBanStatus(response.ban_status)
      setMessage({ type: 'success', text: 'Configuración guardada exitosamente' })
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Error al guardar'
      })
    } finally {
      setSaving(false)
    }
  }

  // Helper to calculate checks per hour
  const checksPerHour = (min: number, max: number) => {
    return Math.round(3600 / ((min + max) / 2) * 10) / 10
  }

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Configuración del Monitor</h2>
            <p className="text-sm text-gray-500 mt-1">
              Ajusta los parámetros críticos para maximizar la detección de citas
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            currentWindow === 'critical'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {currentWindow === 'critical' ? (
              <span className="flex items-center"><Moon className="h-4 w-4 mr-1" /> Ventana Crítica</span>
            ) : (
              <span className="flex items-center"><Sun className="h-4 w-4 mr-1" /> Ventana Normal</span>
            )}
          </div>
        </div>
      </div>

      {/* Ban Status */}
      {banStatus && <BanStatusCard status={banStatus} />}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center">
            <Zap className="h-4 w-4 mr-1" /> Recomendaciones
          </h3>
          {recommendations.map((rec, idx) => (
            <RecommendationCard key={idx} rec={rec} />
          ))}
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`rounded-lg border p-4 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            )}
            <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Target URLs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Target className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">URLs de Monitoreo</h3>
            <Tooltip text="Las URLs donde el monitor busca citas. El Target URL es el widget directo, y el Seed URL es la página de instrucciones para auto-descubrimiento." />
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target URL (Widget de Citas)
                <Tooltip text="URL directa del widget Bookitit. Esta es la página que el monitor revisa cada ciclo buscando el enlace 'ELEGIR FECHA Y HORA'." />
              </label>
              <input
                type="url"
                value={config.target_url}
                onChange={(e) => setConfig({ ...config, target_url: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                placeholder="https://www.citaconsular.es/es/hosteds/widgetdefault/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seed URL (Página de Instrucciones)
                <Tooltip text="Página del consulado donde aparece el iframe del widget. Se usa para auto-descubrir la nueva URL cuando el widget rota." />
              </label>
              <input
                type="url"
                value={config.seed_url}
                onChange={(e) => setConfig({ ...config, seed_url: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                placeholder="https://www.exteriores.gob.es/Consulados/..."
              />
            </div>
          </div>
        </div>

        {/* Section 2: Timing - Critical Window */}
        <div className="bg-purple-50 rounded-lg shadow-sm border border-purple-200 p-6">
          <div className="flex items-center mb-4">
            <Moon className="h-5 w-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Ventana Crítica</h3>
            <Tooltip text="Período de mayor liberación de citas. Durante estas horas, el monitor es más agresivo para no perder oportunidades." />
            <span className="ml-auto text-sm text-purple-600 font-medium">
              ~{checksPerHour(config.schedule.critical_interval_min, config.schedule.critical_interval_max)} checks/hora
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora Inicio
                <Tooltip text="Hora local (Monterrey) cuando inicia la ventana crítica. Recomendado: 5-6 AM." />
              </label>
              <select
                value={config.schedule.critical_start_hour_local}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, critical_start_hour_local: Number(e.target.value) }
                })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora Fin
                <Tooltip text="Hora local cuando termina la ventana crítica. Recomendado: 9-10 AM." />
              </label>
              <select
                value={config.schedule.critical_end_hour_local}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, critical_end_hour_local: Number(e.target.value) }
                })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intervalo Mín (seg)
                <Tooltip text="Tiempo mínimo entre revisiones durante ventana crítica. Menor = más agresivo pero mayor riesgo de baneo." />
              </label>
              <input
                type="number"
                value={config.schedule.critical_interval_min}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, critical_interval_min: Number(e.target.value) }
                })}
                min={10}
                max={120}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intervalo Máx (seg)
                <Tooltip text="Tiempo máximo entre revisiones durante ventana crítica. Se elige aleatoriamente entre mín y máx." />
              </label>
              <input
                type="number"
                value={config.schedule.critical_interval_max}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, critical_interval_max: Number(e.target.value) }
                })}
                min={15}
                max={180}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="mt-3 p-3 bg-white rounded-md">
            <p className="text-xs text-gray-600">
              <strong>Recomendación:</strong> 25-45 segundos = ~80-144 checks/hora (balance óptimo entre velocidad y seguridad)
            </p>
          </div>
        </div>

        {/* Section 3: Normal Window */}
        <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-6">
          <div className="flex items-center mb-4">
            <Sun className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Ventana Normal</h3>
            <Tooltip text="Monitoreo conservador durante el resto del día para mantener vigilancia sin riesgo de baneo." />
            <span className="ml-auto text-sm text-blue-600 font-medium">
              ~{checksPerHour(config.schedule.normal_interval_min, config.schedule.normal_interval_max)} checks/hora
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intervalo Mínimo (segundos)
              </label>
              <input
                type="number"
                value={config.schedule.normal_interval_min}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, normal_interval_min: Number(e.target.value) }
                })}
                min={30}
                max={300}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intervalo Máximo (segundos)
              </label>
              <input
                type="number"
                value={config.schedule.normal_interval_max}
                onChange={(e) => setConfig({
                  ...config,
                  schedule: { ...config.schedule, normal_interval_max: Number(e.target.value) }
                })}
                min={60}
                max={600}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-3 p-3 bg-white rounded-md">
            <p className="text-xs text-gray-600">
              <strong>Recomendación:</strong> 70-160 segundos = ~25-45 checks/hora (bajo riesgo)
            </p>
          </div>
        </div>

        {/* Section 4: Detection Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Search className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Configuración de Detección</h3>
            <Tooltip text="Parámetros que controlan cómo el monitor identifica citas disponibles." />
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Umbral de Horarios (times_threshold)
                <Tooltip text="Número mínimo de patrones HH:MM que deben aparecer para considerar que hay cita. Valor 1 = máxima sensibilidad." />
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  value={config.detection.times_threshold}
                  onChange={(e) => setConfig({
                    ...config,
                    detection: { ...config.detection, times_threshold: Number(e.target.value) }
                  })}
                  min={1}
                  max={5}
                  className="flex-1"
                />
                <span className="w-8 text-center font-bold text-primary-600">
                  {config.detection.times_threshold}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                1 = Máxima sensibilidad (recomendado) | 5 = Muy conservador (puede perder citas)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Palabras Clave
                <Tooltip text="El monitor busca estas palabras en la página para confirmar disponibilidad. Separadas por coma." />
              </label>
              <input
                type="text"
                value={config.detection.keywords.join(', ')}
                onChange={(e) => setConfig({
                  ...config,
                  detection: {
                    ...config.detection,
                    keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                  }
                })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                placeholder="hueco, disponible, available, slot..."
              />
            </div>
          </div>
        </div>

        {/* Section 5: Auto-Recovery */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <RefreshCw className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Auto-Recuperación</h3>
            <Tooltip text="Configuración de auto-redescubrimiento cuando la URL del widget cambia o hay fallos." />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">Auto-Redescubrimiento</span>
                <Tooltip text="Cuando está activo, el monitor automáticamente busca la nueva URL del widget si la actual falla." />
              </div>
              <button
                type="button"
                onClick={() => setConfig({
                  ...config,
                  detection: { ...config.detection, auto_rediscover: !config.detection.auto_rediscover }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.detection.auto_rediscover ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.detection.auto_rediscover ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {config.detection.auto_rediscover && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fallos antes de redescubrir
                    <Tooltip text="Número de fallos consecutivos antes de intentar redescubrir la URL." />
                  </label>
                  <input
                    type="number"
                    value={config.detection.rediscover_fails}
                    onChange={(e) => setConfig({
                      ...config,
                      detection: { ...config.detection, rediscover_fails: Number(e.target.value) }
                    })}
                    min={1}
                    max={10}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Backoff después de redescubrir (seg)
                    <Tooltip text="Tiempo de espera después de un redescubrimiento exitoso antes de continuar el monitoreo normal." />
                  </label>
                  <input
                    type="number"
                    value={config.detection.rediscover_backoff_sec}
                    onChange={(e) => setConfig({
                      ...config,
                      detection: { ...config.detection, rediscover_backoff_sec: Number(e.target.value) }
                    })}
                    min={60}
                    max={1800}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 6: Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Bell className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Notificaciones Telegram</h3>
            <Tooltip text="Configuración de canales de Telegram para recibir alertas." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chat ID Cliente
                <Tooltip text="Chat de Telegram donde se envían SOLO las notificaciones de citas disponibles." />
              </label>
              <input
                type="text"
                value={config.telegram_chat_id || ''}
                onChange={(e) => setConfig({ ...config, telegram_chat_id: e.target.value || null })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="8307935297"
              />
              <p className="text-xs text-gray-500 mt-1">Solo recibe: 🎯 CITA DISPONIBLE</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chat ID Admin/Dev
                <Tooltip text="Chat de Telegram para logs operativos: inicio, paradas, errores, cambios de config." />
              </label>
              <input
                type="text"
                value={config.telegram_dev_chat_id || ''}
                onChange={(e) => setConfig({ ...config, telegram_dev_chat_id: e.target.value || null })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="8307935297"
              />
              <p className="text-xs text-gray-500 mt-1">Recibe: Errores, estado, cambios de config</p>
            </div>
          </div>
        </div>

        {/* Timezone */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Clock className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Zona Horaria</h3>
          </div>

          <select
            value={config.schedule.timezone}
            onChange={(e) => setConfig({
              ...config,
              schedule: { ...config.schedule, timezone: e.target.value }
            })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="America/Monterrey">America/Monterrey (CST/CDT)</option>
            <option value="America/Mexico_City">America/Mexico_City (CST/CDT)</option>
            <option value="America/Cancun">America/Cancun (EST)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </form>
    </div>
  )
}
